import { Controller } from "@hotwired/stimulus"
import { activateFocusTrap, deactivateFocusTrap } from "./utils/focus_trap"
import { applyEnter, animateOut } from "./utils/motion"

// Cmd+K command palette. Opens on ⌘K / Ctrl+K or via [data-command-toggle].
//
// A11y (WAI-ARIA APG Combobox + Listbox):
//   - Focus trap + restore
//   - Esc fecha
//   - Search input recebe foco ao abrir
//   - Resultado anunciado via live region (aria-live no `results` element)
//   - Setas ↑↓ + Enter pra navegar/escolher
//
// i18n: as strings de contagem de resultados ("Nenhum resultado", "1 resultado",
// "N resultados") vêm de data-attributes preenchidos via t() no markup — JS não
// hardcoda idioma. `%{count}` é substituído na hora.
//
// Motion: painel faz fade+scale, backdrop faz fade. Espera a saída antes de
// esconder; respeita `prefers-reduced-motion`.
export default class extends Controller {
  static targets = ["root", "panel", "backdrop", "search", "list", "count"]
  static values  = {
    countZero: { type: String, default: "Nenhum resultado" },
    countOne:  { type: String, default: "1 resultado" },
    countOther: { type: String, default: "%{count} resultados" }
  }

  connect() {
    this.boundHotkey = (e) => {
      // ⌘K / Ctrl+K abre/fecha
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        this.toggle()
        return
      }
      // Esc fecha quando aberto
      if (e.key === "Escape" && !this.rootTarget.classList.contains("hidden")) {
        this.close()
      }
    }
    document.addEventListener("keydown", this.boundHotkey)

    this.boundListKeydown = (e) => this._handleListKeydown(e)
    this.rootTarget.addEventListener("keydown", this.boundListKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHotkey)
    this.rootTarget.removeEventListener("keydown", this.boundListKeydown)
    if (this.trap) deactivateFocusTrap(this.trap)
  }

  toggle() {
    if (this.rootTarget.classList.contains("hidden")) this.open()
    else this.close()
  }

  open() {
    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    if (this.hasPanelTarget) applyEnter(this.panelTarget, "animate-dialog-in")
    if (this.hasBackdropTarget) applyEnter(this.backdropTarget, "animate-backdrop-in")
    this.trap = activateFocusTrap(this.rootTarget)
    // Foca o input de busca (sobrescreve o foco do trap).
    requestAnimationFrame(() => this.searchTarget?.focus())
  }

  async close() {
    await Promise.all([
      this.hasPanelTarget ? animateOut(this.panelTarget, "animate-dialog-out") : null,
      this.hasBackdropTarget ? animateOut(this.backdropTarget, "animate-backdrop-out") : null
    ])
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    deactivateFocusTrap(this.trap)
    this.trap = null
  }

  filter() {
    const term = this.searchTarget.value.toLowerCase()
    let visibleCount = 0
    this.listTarget.querySelectorAll("li").forEach((li) => {
      const match = li.textContent.toLowerCase().includes(term)
      li.style.display = match ? "" : "none"
      if (match) visibleCount++
    })
    if (this.hasCountTarget) {
      // Atualiza live region pra anunciar resultados ao SR.
      this.countTarget.textContent = this._formatCount(visibleCount)
    }
  }

  _formatCount(n) {
    // Strings vêm de data-attributes (i18n via t() no markup). `%{count}` é o
    // placeholder usado pelo Rails I18n na variante `other`.
    if (n === 0) return this.countZeroValue
    if (n === 1) return this.countOneValue
    return this.countOtherValue.replace("%{count}", n)
  }

  _handleListKeydown(e) {
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) return
    const items = Array.from(this.listTarget.querySelectorAll("li:not([style*='display: none']) a, li:not([style*='display: none']) button"))
    if (items.length === 0) return

    const current = items.indexOf(document.activeElement)

    if (e.key === "ArrowDown") {
      e.preventDefault()
      const next = current < 0 ? 0 : Math.min(current + 1, items.length - 1)
      items[next].focus()
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      const prev = current <= 0 ? items.length - 1 : current - 1
      items[prev].focus()
    } else if (e.key === "Enter" && current >= 0) {
      // Browser executa o click default do <a>/<button> focado.
    }
  }
}
